// ---- the water ----
// A piece of ground nobody may walk on. In Jiddo's garden it is his pond, and
// the duck, the penguin, the sea lion, the walrus and the goldfish are let
// through every check here by their `swims` flag; for everyone else its edge,
// grown by their own footprint, is a line no walker's centre crosses.
//
// The one thing that changed on the way into the engine: a game may have no
// water at all. Tata's field has none, and the checks are wired into fieldSpot,
// separate and stepToward, so "no pond" has to be an answer rather than an
// absence. It is: setPond(null), and then nothing is ever wet — onWater and
// crossesWater are false everywhere and ashore never moves anybody, which is
// exactly the behaviour of a field of dry grass.
//
// pondSpot() and bank() are the two that cannot answer without a pond, because
// there is no such place. They throw rather than return a quiet (0,0), which would
// walk a duck to the middle of the field and look like a brain bug. Only a game
// that configured water ever calls them.
export interface Pond{x:number;z:number;a:number;b:number}   // an ellipse on the grass: a and b are its reach along x and z
let P:Pond|null=null;
export const setPond=(p:Pond|null)=>{P=p;};
const need=():Pond=>{if(!P)throw new Error('water: this game has no pond (setPond)');return P;};
// How far (x,z) is from the pond's centre, in units of that grown edge: under 1
// is on the water. With no pond, nothing is ever near one.
export function pondR(x:number,z:number,pad:number){
  if(!P)return Infinity;
  return Math.hypot((x-P.x)/(P.a+pad),(z-P.z)/(P.b+pad));
}
export const onWater=(x:number,z:number,pad:number)=>pondR(x,z,pad)<1;
// somewhere on the pond, well inside its edge
export function pondSpot(){
  const p=need(), th=Math.random()*2*Math.PI, r=0.2+Math.random()*0.5;
  return {x:p.x+r*p.a*Math.cos(th),z:p.z+r*p.b*Math.sin(th)};
}
// does a straight walk from (x0,z0) to (x1,z1) cross it? — the nearest the
// segment comes to the centre, in the same units
export function crossesWater(x0:number,z0:number,x1:number,z1:number,pad:number){
  if(!P)return false;
  const A=P.a+pad, B=P.b+pad, u=(x0-P.x)/A, v=(z0-P.z)/B, du=(x1-x0)/A, dv=(z1-z0)/B;
  const l=du*du+dv*dv, k=l>0?Math.max(0,Math.min(1,-(u*du+v*dv)/l)):0;
  return Math.hypot(u+k*du,v+k*dv)<1;
}
// the point on the grown edge in direction th from the centre, `out` further out
export function bank(th:number,pad:number,out:number){
  const p=need(), c=Math.cos(th), s=Math.sin(th);
  const r=1/Math.hypot(c/(p.a+pad),s/(p.b+pad))+out;
  return {x:p.x+r*c,z:p.z+r*s};
}
// back to the bank, straight out from the centre; true if p had to move
export function ashore(p:{x:number;z:number},pad:number){
  if(!onWater(p.x,p.z,pad))return false;
  let dx=p.x-P!.x, dz=p.z-P!.z; if(Math.hypot(dx,dz)<0.01){dx=0;dz=1;}   // dead centre: toward you
  const s=bank(Math.atan2(dz,dx),pad,0.02); p.x=s.x; p.z=s.z; return true;
}
